import { LinkIcon } from "@heroicons/react/24/solid";
import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { HeaderStatItem } from "@/client/components/common/HeaderStatItem";
import { MetadataHead } from "@/client/components/common/MetdataHead";
import PlatformBadge from "@/client/components/common/PlatformBadge";
import Avatar from "@/client/components/frame-design-system/avatars/Avatar";
import CollectionCard from "@/client/components/frame-design-system/cards/CollectionCard";
import EthLabel from "@/client/components/frame-design-system/labels/EthLabel";
import SectionContainer from "@/client/components/frame-design-system/layout/SectionContainer";
import TabMenu from "@/client/components/frame-design-system/menus/TabMenu";
import { useAuth } from "@/client/components/common/AuthProvider";
import { Instagram } from "@/client/components/icons/instagram";
import { XLogo } from "@/client/components/icons/x-logo";
import { listCollections } from "@/client/lib/api";
import { formatPrettyWebsite, numberWithCommas } from "@/client/lib/helpers";
import { findTotalPieces, findTotalVolume } from "@/client/lib/statsHelpers";
import { Artist } from "@/shared/types/artist";

export type Props = {
  artist: Artist;
  metadata?: MetadataHead;
};

const ArtistPage: NextPage<Props> = ({ artist, metadata }: Props) => {
  const router = useRouter();
  const { address } = useAuth();

  const [collections, setCollections] = useState<any>(null);
  const [totalVolume, setTotalVolume] = useState<number | null>(null);
  const [totalPieces, setTotalPieces] = useState<number | null>(null);
  const [features, setFeatures] = useState<any>(null);
  const [showBio, setShowBio] = useState<boolean>(false);
  const [collectionImage, setCollectionImage] = useState<string | null>(null);

  useEffect(() => {
    fetchCollections();
  }, []);

  // if the connected account changes lets refresh the page to refresh serverside props
  useEffect(() => {
    reloadPage();
  }, [address]);

  const reloadPage = () => {
    router.replace(router.asPath);
  };

  const fetchCollections = async () => {
    let res = await listCollections({
      artistId: artist._id,
      limit: 100, // Adding a high limit as we dont have pagination on this page
    });
    if (!artist?.avatarUrl && res?.length > 0) {
      let coll = res[res.length - 1];
      artist.avatarUrl = coll.imageUrl;
    }
    if (!artist?.bannerImageUrl && res?.length > 0) {
      let coll = res[res.length - 1];
      artist.bannerImageUrl = coll.imageUrl;
    }
    setCollections(res);
  };

  useEffect(() => {
    if (collections?.length > 0) {
      setTotalVolume(findTotalVolume(collections));
      setTotalPieces(findTotalPieces(collections));
      buildPlatformsObject();
      setCollectionImage(collections[collections.length - 1].imageUrl);
    }
  }, [collections]);

  const buildPlatformsObject = () => {
    let seenBadges = {} as any;
    let badgeData = [] as any;

    collections.map((collection: any) => {
      let badge = {
        platform: collection.platformData,
      } as any;

      if (!badge.platform) return;

      let key = badge?.platform?.name;
      if (key in seenBadges) {
        return;
      }
      seenBadges[key] = true;
      badgeData.push(badge);
    });

    setFeatures(badgeData);
  };

  return (
    <>
      <MetadataHead {...metadata} />
      <div className="dark:text-white">
        <div className={`pb-20 pt-20 sm:pt-12`}>
          <SectionContainer removeYPadding={true}>
            <Avatar
              size="96"
              rounding="full"
              imageUrl={
                artist?.avatarUrl
                  ? artist?.avatarUrl
                  : (collectionImage as string)
              }
            />
            <h3 className="font-medium text-header-m tracking-tight mt-4">
              {artist?.name}
            </h3>

            <div className="flex items-center gap-4 text-neutral-500 mt-2">
              {artist?.website ? (
                <>
                  <a
                    href={artist?.website}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-p-m"
                  >
                    <LinkIcon className="h-4 w-4" />
                    {formatPrettyWebsite(artist?.website)}
                  </a>
                  <span className="h-1 w-1 rounded-full bg-neutral-300 block"></span>
                </>
              ) : null}

              <div className="flex items-center gap-3">
                {artist?.twitterUsername ? (
                  <a
                    href={`https://x.com/${artist?.twitterUsername}`}
                    target="_blank"
                    rel="noreferrer"
                    className="transition hover:opacity-80"
                  >
                    <XLogo className="h-4 w-4" />
                  </a>
                ) : null}

                {artist?.instagramUsername ? (
                  <a
                    href={`https://instagram.com/${artist?.instagramUsername}`}
                    target="_blank"
                    rel="noreferrer"
                    className="transition hover:opacity-80"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                ) : null}
              </div>
            </div>
            {artist?.bio ? (
              <>
                <p
                  className={`max-w-3xl ${
                    showBio ? null : "line-clamp-2"
                  } mt-4 text-p-s`}
                >
                  {artist?.bio}
                </p>
                <button
                  onClick={() => setShowBio((prev: boolean) => !prev)}
                  className="font-medium mt-1 text-p-s"
                >
                  {showBio ? "Read less" : "Read more"}
                </button>
              </>
            ) : null}

            <div className="mt-8 flex items-end gap-12 flex-wrap">
              <HeaderStatItem
                title="Works"
                valueSection={
                  <p className="text-p-xl font-medium">
                    {numberWithCommas(totalPieces as number)}
                  </p>
                }
              />

              <HeaderStatItem
                title="Total Volume"
                valueSection={
                  <EthLabel size={"xl"} amount={totalVolume as number} />
                }
              />

              {features && features?.length > 0 ? (
                <HeaderStatItem
                  title="Features"
                  valueSection={
                    <>
                      <div className="flex items-center -space-x-3">
                        {features?.map((badge: any) => {
                          // hydration error occurs here

                          let bgColor = badge?.platform?.platformColor
                            ? badge?.platform?.platformColor
                            : "#000000";

                          let tooltipBase = `Featured in ${badge?.platform?.name}`;
                          if (!badge.platform) return;
                          return (
                            <div
                              key={badge.platform?.name}
                              className="ring-4 ring-[#f9f9f9] mb-1 rounded-full"
                            >
                              <PlatformBadge
                                key={badge.platform?.name}
                                name={badge.platform?.name}
                                logoUrl={badge.platform?.logoImageUrl}
                                bgColor={bgColor}
                                tooltipContent={tooltipBase}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </>
                  }
                />
              ) : null}
            </div>

            <div className="mt-12">
              <TabMenu
                options={[
                  {
                    title: "Works",
                    selected: true,
                    action: () => {},
                  },
                ]}
              />
            </div>
            <div className="w-full mt-12 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-12">
              {collections?.map((collection: any) => {
                return (
                  <CollectionCard
                    key={collection?._id}
                    collection={collection}
                    showArtistName={false}
                    showStats={true}
                  />
                );
              })}
            </div>
          </SectionContainer>
        </div>
      </div>
    </>
  );
};

export default ArtistPage;
